import { useAuth } from '@idu-nightlife/ui-kit';
import { useQueries, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { z } from 'zod';
import { nightlifeConfigs, type NightlifeAppConfig, type NightlifeAppId } from './app-config';

/** A cold App Engine instance answers in seconds; past this the shell stops waiting and offers the venues it has. */
export const NIGHTLIFE_VENUES_TIMEOUT_MS = 10_000;

const NIGHTLIFE_VENUES_KEY = 'nightlife-venues';

const zSessionVenues = z.object({
  data: z.array(z.object({ venueId: z.string().min(1), name: z.string() })),
});

export type NightlifeVenue = z.infer<typeof zSessionVenues>['data'][number];

export type NightlifeVenueList = { config: NightlifeAppConfig; venues: NightlifeVenue[] };

/** The API refused this account (403: an iDU address, an unverified email): it has no venues there. */
export class NightlifeVenuesRefusedError extends Error {}

/** The venues this webapps account manages in one nightlife project, from its API's session exchange. */
export async function fetchNightlifeVenues(
  config: NightlifeAppConfig,
  token: string,
): Promise<NightlifeVenue[]> {
  const response = await fetch(new URL('/api/v2/session/venues', config.apiUrl), {
    headers: { authorization: `Bearer ${token}` },
    signal: AbortSignal.timeout(NIGHTLIFE_VENUES_TIMEOUT_MS),
  });

  if (!response.ok) {
    const message = `${config.id} venue list answered HTTP ${response.status}`;
    throw response.status === 403 ? new NightlifeVenuesRefusedError(message) : new Error(message);
  }

  return zSessionVenues.parse(await response.json()).data;
}

const idsWhere = (
  results: UseQueryResult<NightlifeVenue[]>[],
  holds: (result: UseQueryResult<NightlifeVenue[]>) => boolean,
) =>
  nightlifeConfigs
    .filter((_, index) => results[index] && holds(results[index]))
    .map(({ id }) => id);

// Module scope keeps the reference stable, so react-query re-runs it only when a result changes.
function combineVenueLists(results: UseQueryResult<NightlifeVenue[]>[]) {
  return {
    lists: results.map((result, index) => ({
      config: nightlifeConfigs[index]!,
      venues: result.data ?? [],
    })),
    unsettledAppIds: idsWhere(results, (result) => !result.isFetchedAfterMount),
    unavailableAppIds: idsWhere(
      results,
      // A failed refetch keeps the venues it had, so only an instance with none to offer is unavailable.
      (result) =>
        result.isError && !result.data && !(result.error instanceof NightlifeVenuesRefusedError),
    ),
  };
}

/**
 * Each nightlife project's venue list, the projects not yet answered since mount, and those that
 * could not answer (down or timed out, after one retry). Neither failure holds the shell; opening
 * the selector fetches again.
 */
export function useNightlifeVenues(): {
  lists: NightlifeVenueList[];
  unsettledAppIds: NightlifeAppId[];
  unavailableAppIds: NightlifeAppId[];
} {
  const { firebaseUser, getAccessToken } = useAuth();

  return useQueries({
    queries: nightlifeConfigs.map((config) => ({
      queryKey: [NIGHTLIFE_VENUES_KEY, config.id, firebaseUser?.uid],
      enabled: !!firebaseUser,
      retry: (failures: number, error: Error) =>
        failures < 1 && !(error instanceof NightlifeVenuesRefusedError),
      refetchOnMount: 'always' as const,
      queryFn: async () => {
        const token = getAccessToken();
        if (!token) throw new Error('No shell session to list nightlife venues with');
        return fetchNightlifeVenues(config, token).catch((error: unknown) => {
          console.warn(`[useNightlifeVenues] ${config.id} offers no venues`, error);
          throw error;
        });
      },
    })),
    combine: combineVenueLists,
  });
}

export function useRefreshNightlifeVenues() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: [NIGHTLIFE_VENUES_KEY] });
}
